import React, { useState } from 'react'
import { X, Download, FileText, FileSpreadsheet, FileX } from 'lucide-react'

const ExportModal = ({ 
  isOpen, 
  onClose, 
  onExport, 
  isLoading = false,
  title = "Export Data"
}) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf')
  const [filename, setFilename] = useState('expenses')

  const formats = [
    {
      id: 'pdf',
      name: 'PDF Document',
      description: 'Formatted report with styling',
      icon: FileText,
      extension: '.pdf'
    },
    {
      id: 'excel',
      name: 'Excel Spreadsheet',
      description: 'Editable spreadsheet format',
      icon: FileSpreadsheet,
      extension: '.xlsx'
    },
    {
      id: 'csv',
      name: 'CSV File',
      description: 'Simple comma-separated values',
      icon: FileX,
      extension: '.csv'
    }
  ]

  const handleExport = () => {
    onExport(selectedFormat, filename)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
            disabled={isLoading}
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Format Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Export Format
          </label>
          <div className="space-y-3">
            {formats.map((format) => {
              const IconComponent = format.icon
              return (
                <label
                  key={format.id}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedFormat === format.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="format"
                    value={format.id}
                    checked={selectedFormat === format.id}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    className="sr-only"
                  />
                  <IconComponent className={`w-5 h-5 mr-3 ${
                    selectedFormat === format.id ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{format.name}</div>
                    <div className="text-sm text-gray-500">{format.description}</div>
                  </div>
                  <div className="text-xs text-gray-400 font-mono">
                    {format.extension}
                  </div>
                </label>
              )
            })}
          </div>
        </div>

        {/* Filename Input */}
        <div className="mb-6">
          <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-2">
            File Name (without extension)
          </label>
          <input
            type="text"
            id="filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="expenses"
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Final filename: {filename || 'expenses'}_YYYY-MM-DD{formats.find(f => f.id === selectedFormat)?.extension}
          </p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isLoading || !filename.trim()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExportModal
